import {
  Action,
  config,
  defaultProcessingSaga,
  defaultResponseProcessing,
  fetchAuthSaga,
  FetchResponse,
} from '@triare/auth-redux';
import { put, delay, takeLatest, call } from 'redux-saga/effects';
import { actions, CommentEntity, CommentStatus } from './index';
import { PaginatedResponse, PaginationProps } from '../types';

export type CommentListData = PaginatedResponse<Comment>;

export type CommentListResponse = CommentListData;
export type CommentOrderColumn =
  | 'id'
  | 'text'
  | 'numberOfLikes'
  | 'numberOfDislikes'
  | 'numberOfComments'
  | 'createdAt'
  | 'updatedAt';

export type CommentListParams = PaginationProps<CommentOrderColumn> & {
  text?: string;
  category?: string;
  subCategory?: string;
  article?: string;
  business?: string;
  postId?: string;
  user?: string;
  international?: string;
  entity: CommentEntity;
  status?: CommentStatus | null;
};

export const commentList = (
  payload: CommentListParams
): Action<CommentListParams> => ({
  type: actions.commentListTrigger.toString(),
  payload,
});

export const commentListClear = (): Action => ({
  type: actions.commentListClear.toString(),
});

export const commentListErrorClear = (): Action => ({
  type: actions.commentListErrorReset.toString(),
});

export function* commentListRequestSaga({
  payload,
}: Action<CommentListParams>) {
  yield put({
    type: actions.commentListStart.toString(),
  });

  if (!payload) {
    yield put({
      type: actions.commentListError.toString(),
      payload: {
        message: 'CommentList: Parameters not passed.',
      },
    });

    return;
  }

  const { roles, ...restParams } = payload as Record<string, string>;

  const params = new URLSearchParams(
    Object.entries(restParams).filter((entry) => !!(entry[1] as string))
  );

  const response = (yield call(
    fetchAuthSaga,
    `${process.env.REACT_APP_API}comments?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    {},
    config.fetchDelay
  )) as FetchResponse;

  yield defaultResponseProcessing<CommentListData, CommentListResponse>(
    response,
    actions.commentListSuccess.toString(),
    actions.commentListError.toString(),
    false,
    (commentListData) => commentListData
  );
}

export default function* saga() {
  yield takeLatest(
    actions.commentListTrigger.toString(),
    defaultProcessingSaga(commentListRequestSaga, {
      error: actions.commentListError.toString(),
      locationCancel: actions.commentListClear.toString(),
    })
  );

  yield takeLatest(actions.commentListSuccess.toString(), function* clear() {
    if (config.delaySuccess) {
      yield delay(config.delaySuccess);
    }
  });

  yield takeLatest(actions.commentListError.toString(), function* errorReset() {
    if (config.delayError) {
      yield delay(config.delayError);
    }

    yield put({
      type: actions.commentListErrorReset.toString(),
    });
  });
}
